import type {
	PortalAddToCartDto,
	PortalCartResponseDto,
	PortalCheckoutOptionsResponseDto,
	PortalSetCartAddressesDto,
	PortalSubmitCartDto,
	PortalSubmitCartResponseDto,
	PortalUpdateCartItemDto,
	PortalValidateCartResponseDto,
} from '@supply-mover-2-0/shared';
import { portalFetch } from '../lib/apiClient';

/**
 * Fetch the current cart contents for the active client.
 */
export async function getCart(token: string): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>('/cart', token);
}

/**
 * Preview a specific client's cart without switching the active client.
 * Used by the location selector to show cart previews.
 */
export async function previewCart(
	token: string,
	clientId: number,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>(`/cart/preview/${clientId}`, token);
}

/**
 * Add an item to the cart.
 */
export async function addToCart(
	token: string,
	payload: PortalAddToCartDto,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>('/cart/items', token, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
}

/**
 * Update the quantity of a cart line item.
 * Uses the order detail ID (not the item ID).
 */
export async function updateCartItem(
	token: string,
	detailId: number,
	payload: PortalUpdateCartItemDto,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>(`/cart/items/${detailId}`, token, {
		method: 'PUT',
		body: JSON.stringify(payload),
	});
}

/**
 * Remove a line item from the cart by detail ID.
 */
export async function removeFromCart(
	token: string,
	detailId: number,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>(`/cart/items/${detailId}`, token, {
		method: 'DELETE',
	});
}

/**
 * Clear the entire cart.
 */
export async function clearCart(token: string): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>('/cart', token, {
		method: 'DELETE',
	});
}

/**
 * Update billing and shipping addresses on the cart.
 * Triggers recalculation since shipping address affects freight/fees.
 */
export async function updateAddresses(
	token: string,
	payload: PortalSetCartAddressesDto,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>('/cart/addresses', token, {
		method: 'PUT',
		body: JSON.stringify(payload),
	});
}

/**
 * Get all checkout options: addresses, shipping methods, payment types,
 * saved payment methods, credit info, and settings.
 */
export async function getCheckoutOptions(
	token: string,
): Promise<PortalCheckoutOptionsResponseDto> {
	return portalFetch<PortalCheckoutOptionsResponseDto>(
		'/cart/checkout-options',
		token,
	);
}

/**
 * Pre-flight validation — runs server-side checks without submitting.
 */
export async function validateCart(
	token: string,
): Promise<PortalValidateCartResponseDto> {
	return portalFetch<PortalValidateCartResponseDto>('/cart/validate', token, {
		method: 'POST',
		body: JSON.stringify({}),
	});
}

/**
 * Submit the cart as an order.
 */
export async function submitCart(
	token: string,
	payload: PortalSubmitCartDto,
): Promise<PortalSubmitCartResponseDto> {
	return portalFetch<PortalSubmitCartResponseDto>('/cart/submit', token, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
}

/**
 * Apply a coupon code to the active cart. Server-side normalizes
 * (trim + uppercase) and de-duplicates. Subject to the per-user rate
 * limit on the controller (10/min, 60/hr) — exceeding the bucket throws
 * 403 with a friendly message.
 */
export async function applyCartCoupon(
	token: string,
	code: string,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>('/cart/coupons', token, {
		method: 'POST',
		body: JSON.stringify({ code }),
	});
}

/**
 * Remove a previously-entered coupon code from the active cart.
 * Case-insensitive match.
 */
export async function removeCartCoupon(
	token: string,
	code: string,
): Promise<PortalCartResponseDto> {
	return portalFetch<PortalCartResponseDto>(
		`/cart/coupons/${encodeURIComponent(code)}`,
		token,
		{
			method: 'DELETE',
		},
	);
}
